import { flowIdeas } from '@repletoryreach/sdk-api';
import { Button, Chip, Group, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { Checkbox, NumberInput, SegmentedControl, Select, Switch } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../i18n/I18nProvider';
import { formatCredits } from '../../../lib/credits';
import { FLOW_LANGUAGES, MAX_COUNT, MIN_COUNT, TONES } from './lib/data';

const MUTED = 'var(--rr-color-text-muted)';

/**
 * Every setting of a suggestion set, in the four groups the owner chose (30 October 2026): what the AI uses, goal and
 * detail, channels, language and tone, and safety and hand-over; then the model that writes the suggestions. Each hint
 * sits under its input. On a new set the changes are kept as they are made; on a set already started, Save sends them
 * and the AI gathers its view of the business again on its next turn.
 */
export function SettingsDrawer({ opened, onClose, settings, onChange, options, insightsDate, onSave, saving }: {
  opened: boolean;
  onClose: () => void;
  settings: flowIdeas.FlowIdeaSettings;
  onChange: (next: flowIdeas.FlowIdeaSettings) => void;
  options?: flowIdeas.FlowIdeaOptions;
  insightsDate?: string;
  /** A set already started: Save, rather than Done. */
  onSave?: () => void;
  saving?: boolean;
}) {
  const t = useT();
  const set = <K extends keyof flowIdeas.FlowIdeaSettings>(key: K, value: flowIdeas.FlowIdeaSettings[K]) => onChange({ ...settings, [key]: value });
  const toggle = <T extends string>(list: T[], value: T, on: boolean) => (on ? [...new Set([...list, value])] : list.filter((x) => x !== value));
  const models = options?.models ?? [];
  const docs = options?.documents ?? [];
  const price = (minor: number | null | undefined, key: string) => (minor == null ? t('flowIdeas.settings.priceUnknown') : t(key, { credits: formatCredits(minor, t) }));
  const modelLabel = (m: flowIdeas.FlowIdeaModel) => (m.is_default ? t('flowIdeas.settings.modelStandard', { name: m.name }) : m.name);
  const suggest = models.find((m) => m.code === settings.suggest_model);
  const build = models.find((m) => m.code === settings.build_model);

  return (
    <Drawer opened={opened} onClose={onClose} title={t('flowIdeas.settings.title')} position="right" size="md">
      <Stack gap="lg" data-testid="ideas-settings">
        <Stack gap="sm">
          <Title order={5}>{t('flowIdeas.settings.uses.title')}</Title>
          <Switch label={t('flowIdeas.settings.uses.knowledge')} description={t('flowIdeas.settings.uses.knowledgeHint', { count: docs.length })}
            checked={settings.use_knowledge} onChange={(on) => set('use_knowledge', on)} data-testid="ideas-use-knowledge" />
          {settings.use_knowledge && docs.length > 0 && (
            <Stack gap={6} style={{ paddingLeft: 16 }}>
              <Switch label={t('flowIdeas.settings.uses.allDocuments')} description={t('flowIdeas.settings.uses.allDocumentsHint')}
                checked={settings.documents.length === 0}
                onChange={(on) => set('documents', on ? [] : docs.slice(0, 1).map((d) => d.id))} />
              {settings.documents.length > 0 && (
                <div className="rr-ideas__doclist" role="group" aria-label={t('flowIdeas.settings.uses.documents')} style={{ maxHeight: 220, overflowY: 'auto' }}>
                  <Stack gap={4}>
                    {docs.map((d) => (
                      <Checkbox key={d.id} label={d.name} checked={settings.documents.includes(d.id)}
                        onChange={(on) => set('documents', toggle(settings.documents, d.id, on))} />
                    ))}
                  </Stack>
                </div>
              )}
            </Stack>
          )}
          {options?.insights && (
            <Switch label={t('flowIdeas.settings.uses.insights')} description={t('flowIdeas.settings.uses.insightsHint', { date: insightsDate ?? '' })}
              checked={settings.use_insights} onChange={(on) => set('use_insights', on)} data-testid="ideas-use-insights" />
          )}
          <Switch label={t('flowIdeas.settings.uses.flows', { count: options?.flows ?? 0 })} description={t('flowIdeas.settings.uses.flowsHint')}
            checked={settings.use_flows} onChange={(on) => set('use_flows', on)} />
          {(options?.apis ?? 0) > 0 && (
            <Switch label={t('flowIdeas.settings.uses.apis', { count: options?.apis ?? 0 })} description={t('flowIdeas.settings.uses.apisHint')}
              checked={settings.use_apis} onChange={(on) => set('use_apis', on)} />
          )}
          {options?.bookings_on && (
            <Switch label={t('flowIdeas.settings.uses.bookings')} description={t('flowIdeas.settings.uses.bookingsHint')}
              checked={settings.use_bookings} onChange={(on) => set('use_bookings', on)} />
          )}
          {options?.tickets_on && (
            <Switch label={t('flowIdeas.settings.uses.tickets')} description={t('flowIdeas.settings.uses.ticketsHint')}
              checked={settings.use_tickets} onChange={(on) => set('use_tickets', on)} />
          )}
        </Stack>

        <Stack gap="sm">
          <Title order={5}>{t('flowIdeas.settings.goal.title')}</Title>
          <Stack gap={4}>
            <Text role="label">{t('flowIdeas.settings.goal.goals')}</Text>
            <Group gap="xs" wrap="wrap" role="group" aria-label={t('flowIdeas.settings.goal.goals')}>
              {flowIdeas.FLOW_IDEA_GOALS.map((g) => (
                <Chip key={g} size="sm" checked={settings.goals.includes(g)} onChange={(on) => set('goals', toggle(settings.goals, g, on))}>
                  {t(`flowIdeas.goal.${g}`)}
                </Chip>
              ))}
            </Group>
            <Text role="caption" c={MUTED}>{t('flowIdeas.settings.goal.goalsHint')}</Text>
          </Stack>
          <NumberInput label={t('flowIdeas.settings.goal.count')} description={t('flowIdeas.settings.goal.countHint', { min: MIN_COUNT, max: MAX_COUNT })}
            min={MIN_COUNT} max={MAX_COUNT} value={settings.count} data-testid="ideas-count"
            onChange={(v) => set('count', Math.min(MAX_COUNT, Math.max(MIN_COUNT, typeof v === 'number' ? Math.round(v) : MIN_COUNT)))} />
          <Stack gap={4}>
            <Text role="label">{t('flowIdeas.settings.goal.detail')}</Text>
            <SegmentedControl value={settings.detail} onChange={(v) => set('detail', v as flowIdeas.FlowIdeaDetail)}
              data={(['simple', 'complete'] as const).map((d) => ({ value: d, label: t(`flowIdeas.detail.${d}.name`) }))} />
            <Text role="caption" c={MUTED}>{t(`flowIdeas.detail.${settings.detail}.hint`)}</Text>
          </Stack>
        </Stack>

        <Stack gap="sm">
          <Title order={5}>{t('flowIdeas.settings.voice.title')}</Title>
          <Stack gap={4}>
            <Text role="label">{t('flowIdeas.settings.voice.channels')}</Text>
            <Group gap="xs" wrap="wrap" role="group" aria-label={t('flowIdeas.settings.voice.channels')}>
              {flowIdeas.FLOW_IDEA_CHANNELS.map((c) => (
                <Chip key={c} size="sm" checked={settings.channels.includes(c)} onChange={(on) => set('channels', toggle(settings.channels, c, on))}>
                  {t(`flowIdeas.channel.${c}`)}
                </Chip>
              ))}
            </Group>
            <Text role="caption" c={MUTED}>{t('flowIdeas.settings.voice.channelsHint')}</Text>
          </Stack>
          <Select label={t('flowIdeas.settings.voice.language')} description={t('flowIdeas.settings.voice.languageHint')}
            value={settings.language} onChange={(e) => set('language', e.currentTarget.value)}
            data={[...new Set([...FLOW_LANGUAGES, settings.language])].map((l) => ({ value: l, label: t(`flowIdeas.lang.${l}`, l) }))} />
          <Select label={t('flowIdeas.settings.voice.tone')} description={t('flowIdeas.settings.voice.toneHint')}
            value={settings.tone} onChange={(e) => set('tone', e.currentTarget.value as flowIdeas.FlowIdeaTone)}
            data={TONES.map((x) => ({ value: x, label: t(`flowIdeas.tone.${x}`) }))} />
          {(options?.bots.length ?? 0) > 1 && (
            <Select label={t('flowIdeas.settings.voice.bot')} description={t('flowIdeas.settings.voice.botHint')}
              value={settings.bot_id ?? options?.bots.find((b) => b.is_default)?.id ?? ''}
              onChange={(e) => set('bot_id', e.currentTarget.value || null)}
              data={(options?.bots ?? []).map((b) => ({ value: b.id, label: b.is_default ? t('flowIdeas.settings.voice.botDefault', { name: b.name }) : b.name }))} />
          )}
        </Stack>

        <Stack gap="sm">
          <Title order={5}>{t('flowIdeas.settings.safety.title')}</Title>
          <Switch label={t('flowIdeas.settings.safety.person')} description={t('flowIdeas.settings.safety.personHint')}
            checked={settings.offer_person} onChange={(on) => set('offer_person', on)} data-testid="ideas-safety-person" />
          <Switch label={t('flowIdeas.settings.safety.hours')} description={t('flowIdeas.settings.safety.hoursHint')}
            checked={settings.office_hours} onChange={(on) => set('office_hours', on)} />
          <Switch label={t('flowIdeas.settings.safety.consent')} description={t('flowIdeas.settings.safety.consentHint')}
            checked={settings.consent} onChange={(on) => set('consent', on)} />
        </Stack>

        <Stack gap="sm">
          <Title order={5}>{t('flowIdeas.settings.models.title')}</Title>
          <Select label={t('flowIdeas.settings.models.suggest')} data-testid="ideas-suggest-model"
            description={price(suggest?.turn_minor, 'flowIdeas.settings.models.suggestPrice')}
            value={settings.suggest_model} onChange={(e) => set('suggest_model', e.currentTarget.value)}
            data={models.map((m) => ({ value: m.code, label: modelLabel(m) }))} />
          <Select label={t('flowIdeas.settings.models.build')} data-testid="ideas-build-model"
            description={price(build?.build_minor, 'flowIdeas.settings.models.buildPrice')}
            value={settings.build_model} onChange={(e) => set('build_model', e.currentTarget.value)}
            data={models.map((m) => ({ value: m.code, label: modelLabel(m) }))} />
          <Text role="caption" c={MUTED}>{t('flowIdeas.settings.models.note')}</Text>
        </Stack>

        <Group justify="flex-end" gap="sm">
          {onSave ? (
            <>
              <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
              <Button onClick={onSave} loading={saving} data-testid="ideas-settings-save">{t('flowIdeas.settings.save')}</Button>
            </>
          ) : (
            <Button onClick={onClose} data-testid="ideas-settings-done">{t('flowIdeas.settings.done')}</Button>
          )}
        </Group>
      </Stack>
    </Drawer>
  );
}
